import React, { useState } from "react";
import { astryx } from "@shadowcue/ui";
import type { ISectionStat } from "@shadowcue/ui";

interface RegradeModalProps {
  isOpen: boolean;
  sectionStats: ISectionStat[];
  loading: boolean;
  onRegrade: (questionIds: number[]) => void;
  onClose: () => void;
}

const pluralize = (count: number) => `${count} question${count === 1 ? "" : "s"}`;

const RegradeModal = ({
  isOpen,
  sectionStats,
  loading,
  onRegrade,
  onClose,
}: RegradeModalProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const allQuestionIds = sectionStats.flatMap((s) => s.questionStats.map((q) => q.id));
  const selectedCount = selectedIds.size;
  const totalCount = allQuestionIds.length;
  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const hasSelection = selectedCount > 0;

  const handleOpenChange = (nextIsOpen: boolean) => {
    if (!nextIsOpen) {
      onClose();
    }
  };

  const applySelection = (ids: number[], select: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (select ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const setSectionSelection = (sectionIds: number[], nextIds: number[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      sectionIds.forEach((id) => next.delete(id));
      nextIds.forEach((id) => next.add(id));
      return next;
    });
  };

  return (
    <astryx.Dialog
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      purpose="form"
      width={560}
    >
      <astryx.Layout
        header={
          <astryx.DialogHeader
            title="Regrade questions"
            subtitle="Select the questions you want to regrade against the full conversation transcript."
            onOpenChange={handleOpenChange}
          />
        }
        content={
          <astryx.LayoutContent>
            <astryx.VStack gap={4}>
              <astryx.HStack gap={2} align="center">
                {/* Deliberately no indeterminate state: parent checkboxes only
                    read as checked when the whole group is selected; partial
                    selection is conveyed by the count text instead. */}
                <astryx.CheckboxInput
                  label="Select all"
                  value={allSelected}
                  onChange={(checked) => applySelection(allQuestionIds, checked)}
                  isDisabled={loading || totalCount === 0}
                />
                <astryx.Text type="supporting" color="secondary">
                  {`${selectedCount} of ${totalCount} selected`}
                </astryx.Text>
              </astryx.HStack>

              <astryx.Divider />

              <astryx.VStack gap={4}>
                {sectionStats.map((section, idx) => {
                  const sectionIds = section.questionStats.map((q) => q.id);
                  const sectionSelectedCount = sectionIds.filter((id) =>
                    selectedIds.has(id),
                  ).length;
                  const sectionAllSelected =
                    sectionIds.length > 0 && sectionSelectedCount === sectionIds.length;
                  const sectionSomeSelected =
                    sectionSelectedCount > 0 && !sectionAllSelected;

                  return (
                    <astryx.VStack gap={2} key={section.id}>
                      <astryx.HStack gap={2} align="center">
                        <astryx.CheckboxInput
                          label={`Section ${idx + 1} — ${section.name}`}
                          value={sectionAllSelected}
                          onChange={(checked) => applySelection(sectionIds, checked)}
                          isDisabled={loading || sectionIds.length === 0}
                        />
                        {sectionSomeSelected && (
                          <astryx.Text type="supporting" color="secondary">
                            {`${sectionSelectedCount} of ${sectionIds.length} selected`}
                          </astryx.Text>
                        )}
                      </astryx.HStack>
                      <astryx.CheckboxList
                        label={`${section.name} questions`}
                        isLabelHidden
                        isDisabled={loading}
                        value={section.questionStats
                          .filter((q) => selectedIds.has(q.id))
                          .map((q) => String(q.id))}
                        onChange={(values) =>
                          setSectionSelection(sectionIds, values.map(Number))
                        }
                      >
                        {section.questionStats.map((qs) => (
                          <astryx.CheckboxListItem
                            key={qs.id}
                            value={String(qs.id)}
                            label={<astryx.Text>{qs.body || "Untitled question"}</astryx.Text>}
                            aria-label={qs.body || "Untitled question"}
                          />
                        ))}
                      </astryx.CheckboxList>
                    </astryx.VStack>
                  );
                })}
              </astryx.VStack>

              <astryx.Text type="supporting" color="secondary">
                Regrading re-runs AI scoring against the full transcript and
                overwrites the selected questions&rsquo; current scores.
              </astryx.Text>
            </astryx.VStack>
          </astryx.LayoutContent>
        }
        footer={
          <astryx.LayoutFooter hasDivider>
            <astryx.HStack gap={2} justify="end">
              <astryx.Button label="Cancel" onClick={onClose} />
              <astryx.Button
                label={hasSelection ? `Regrade ${pluralize(selectedCount)}` : "Regrade questions"}
                variant="primary"
                onClick={() => onRegrade(Array.from(selectedIds))}
                isLoading={loading}
                isDisabled={!hasSelection}
              />
            </astryx.HStack>
          </astryx.LayoutFooter>
        }
      />
    </astryx.Dialog>
  );
};

export default RegradeModal;
